import clsx from "clsx";
import React from "react";

export type TextProps = {
  readonly id?: string;
  readonly htmlFor?: string;
  readonly children?: React.ReactNode;
  readonly component?: Component;
  readonly className?: string;
  readonly variant?: Variant;
  readonly text?: string | number | null;
};

export const Text = ({
  children,
  component = "span",
  variant = "body1",
  className,
  text = "",
  id,
  htmlFor,
}: TextProps): React.ReactElement => {
  const classes = clsx(variantClasses[variant], className);

  const content = children ?? text;

  return React.createElement(component, { className: classes, id, htmlFor }, content);
};

const variantClasses: Record<NonNullable<TextProps["variant"]>, string> = {
  "error": "font-sans text-[11px] font-normal leading-[1.4] text-red-500",

  "body1": "font-sans text-body1 text-primary-text",
  "bold1": "font-sans text-bold1 text-primary-text",
  "body2": "font-sans text-body2 text-primary-text",
  "bold2": "font-sans text-bold2 text-primary-text",
  "body3": "font-sans text-body3 text-primary-text",
  "h1": "font-sans text-h1 text-primary-text",
  "h2": "font-sans text-h2 text-primary-text",
  "h3": "font-sans text-h3 text-primary-text",
  "label": "font-sans text-body2 font-medium text-primary-text",
  "tab": "font-sans text-body2 font-medium text-primary-text",
  "tableHeader": "font-sans text-chip font-medium tracking-[0.72px] uppercase text-primary-text",
  "lrgButtonText": "font-sans text-button text-primary-text",
  "medButtonText": "font-sans text-body3 font-bold text-primary-text",
  "smlButtonText": "font-sans text-chip font-medium text-primary-text",
  "display": "font-sans text-display text-primary-text",
  "v3-h1": "font-serif text-4xl italic text-primary",
  "v3-h2": "font-serif text-3xl italic text-primary",
  "v3-h3": "font-serif text-2xl italic text-primary",
  "v3-h4": "font-serif text-2xl font-normal leading-tight font-bold",
};

type Variant =
  | "display"
  | "h1"
  | "h2"
  | "body1"
  | "bold1"
  | "body2"
  | "bold2"
  | "body3"
  | "error"
  | "tab"
  | "tableHeader"
  | "lrgButtonText"
  | "medButtonText"
  | "smlButtonText"
  | "label"
  | "h3"
  | "v3-h1"
  | "v3-h2"
  | "v3-h3"
  | "v3-h4";

type Component =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "v3-tab"
  | "v3-tableHeader"
  | "v3-lrgButtonText"
  | "v3-medButtonText"
  | "v3-smlButtonText"
  | "v3-label"
  | "p"
  | "span"
  | "label";
